import { addBreadcrumb } from '@sentry/react';
import { Preferences } from '@capacitor/preferences';

import { PUSH_NOTIFICATION_EVENTS_STORAGE_KEY } from './push-notification-events.constants';
import { PushNotificationEventSchema } from './push-notification-events.schema';
import { PushNotificationEvent } from './push-notification-events.types';

const parseStoredEventsBlob = (value: string): unknown[] | null => {
  try {
    const parsed: unknown = JSON.parse(value);
    return Array.isArray(parsed) ? parsed : null;
  } catch {
    addBreadcrumb({
      category: 'push_notification_events',
      message: 'Failed to JSON.parse stored push_notification_events blob',
    });
    return null;
  }
};

// Returns only schema-valid events. Per-item validation failures are filtered
// silently and converge on the next save from the processing hook.
export const getStoredPushNotificationEvents = async (): Promise<PushNotificationEvent[]> => {
  const { value } = await Preferences.get({ key: PUSH_NOTIFICATION_EVENTS_STORAGE_KEY });
  if (!value) {
    return [];
  }

  const rawEvents = parseStoredEventsBlob(value);
  if (rawEvents === null) {
    // Storage holds an unparseable JSON string (e.g. truncated write, foreign
    // writer). Reset the key here so it can't get stuck in this state — the
    // processing hook's early-return on an empty result would otherwise leave
    // the bad blob in place indefinitely.
    await saveStoredPushNotificationEvents([]);
    return [];
  }

  return rawEvents
    .map((item) => PushNotificationEventSchema.safeParse(item))
    .filter((result): result is { success: true; data: PushNotificationEvent } => result.success)
    .map((result) => result.data);
};

export const saveStoredPushNotificationEvents = async (events: PushNotificationEvent[]): Promise<void> => {
  await Preferences.set({
    key: PUSH_NOTIFICATION_EVENTS_STORAGE_KEY,
    value: JSON.stringify(events),
  });
};

export const clearStoredPushNotificationEvents = async (): Promise<void> => {
  await Preferences.remove({ key: PUSH_NOTIFICATION_EVENTS_STORAGE_KEY });
};

// Serializes concurrent appends to prevent the read-modify-write from clobbering
// events when multiple notifications/actions arrive simultaneously.
let appendQueue: Promise<void> = Promise.resolve();

export const appendStoredPushNotificationEvent = (event: PushNotificationEvent): Promise<void> => {
  appendQueue = appendQueue.then(async () => {
    const existing = await getStoredPushNotificationEvents();
    await saveStoredPushNotificationEvents([...existing, event]);
  });
  return appendQueue;
};
